import { ActionPermissionsObject, PermissionActionType } from "common/interfaces";

const IMAGE_SOURCE_ORDER = ["ai", "team", "web", "stock", "none"];
// A Team Asset miss falls through to one of these, so Team Assets can never be a fallback itself.
// Generated imagery is the default, but a workspace can turn generative AI off in advanced settings -
// stock photography is the closest stand-in for it, so it comes before web search here.
const FALLBACK_IMAGE_SOURCE_ORDER = ["ai", "stock", "web", "none"];

function hasPermission(actionPermissions, action) {
    const entry = actionPermissions?.[action];
    return Boolean(entry?.use ?? entry?.[PermissionActionType.USE]);
}

function getAllowedImageSources(actionPermissions, excludedSources = []) {
    const canGenerate = hasPermission(actionPermissions, ActionPermissionsObject.GENERATIVE_AI) &&
        hasPermission(actionPermissions, ActionPermissionsObject.GENERATIVE_AI_IMAGES);

    return {
        ai: canGenerate && !excludedSources.includes("ai"),
        team: hasPermission(actionPermissions, ActionPermissionsObject.SHARED_RESOURCES) && !excludedSources.includes("team"),
        web: hasPermission(actionPermissions, ActionPermissionsObject.WEB_IMAGES) && !excludedSources.includes("web"),
        stock: hasPermission(actionPermissions, ActionPermissionsObject.STOCK_LIBRARIES) && !excludedSources.includes("stock"),
        none: !excludedSources.includes("none")
    };
}

function resolveImageSource(imageSource, actionPermissions, excludedSources = []) {
    const allowed = getAllowedImageSources(actionPermissions, excludedSources);
    // Team Assets are available on Team and Enterprise plans. Pro workspaces do not have that
    // permission, so their default remains generated imagery.
    const requested = imageSource || (allowed.team ? "team" : "ai");

    if (allowed[requested]) {
        return requested;
    }

    // A generated-imagery preference the workspace prohibits substitutes the closest stand-in -
    // stock photography, then web search - rather than Team Assets, which the preference passed
    // over. Anything else unavailable falls through the normal order.
    const order = requested === "ai" ? FALLBACK_IMAGE_SOURCE_ORDER : IMAGE_SOURCE_ORDER;
    return order.find(candidate => allowed[candidate]) || "none";
}

/**
 * The source used when Team Assets finds no match for a slide. Always returns something usable: an
 * unset fallback isn't "use the default", it leaves the slide with no image at all, so every entry
 * point resolves through here rather than deciding for itself.
 */
export function resolveFallbackImageSource(fallbackImageSource, actionPermissions) {
    const allowed = getAllowedImageSources(actionPermissions, ["team"]);
    const requested = fallbackImageSource || "ai";

    if (allowed[requested]) {
        return requested;
    }

    return FALLBACK_IMAGE_SOURCE_ORDER.find(candidate => allowed[candidate]) || "none";
}

/**
 * The permissions a brand kit is edited against: AI images count as allowed even where the
 * workspace prohibits them. A kit records which sources the brand wants rather than which ones
 * this workspace permits today, and a guardrail can be switched on and off under a kit that
 * outlives it - so the choice is kept here and substituted at generation time instead.
 */
function withAiImagesAllowed(actionPermissions) {
    return {
        ...actionPermissions,
        [ActionPermissionsObject.GENERATIVE_AI_IMAGES]: { [PermissionActionType.USE]: true }
    };
}

/** The preferred source a brand kit shows in its own editor. See withAiImagesAllowed. */
export function resolveBrandKitImageSource(imageSource, actionPermissions) {
    return resolveImageSource(imageSource, withAiImagesAllowed(actionPermissions));
}

/** The alternate source a brand kit shows in its own editor. See withAiImagesAllowed. */
export function resolveBrandKitFallbackImageSource(fallbackImageSource, actionPermissions) {
    return resolveFallbackImageSource(fallbackImageSource, withAiImagesAllowed(actionPermissions));
}

export default resolveImageSource;
